import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
  makeStyles,
} from "@material-ui/core";
import React, { useState } from "react";
import { Button, FileField, FileInput, required } from "react-admin";
import CustomPdfPreview from "./CustomPdfPreview";
import { useField } from "react-final-form";

interface IPdfInputField {
  label?: string;
  uploadProgress: number;
  source: string;
  urlSource?: string;
  isRequired?: boolean;
  maxSize: number;
}

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  cancelButton: {
    backgroundColor: "#eeeeee",
    color: "black",
  },
  dangerButton: {
    backgroundColor: "#f44336",
  },
});

const CustomPdfInput = ({
  label,
  uploadProgress,
  source,
  urlSource,
  maxSize,
  isRequired = false,
}: IPdfInputField) => {
  const ownInput = useField(source);
  const pdfUrlField = useField(urlSource);
  const classes = useStyles();

  const [file, setFile] = useState<File | null>(null);
  const [fileExist, setFileExist] = useState<boolean>(false);
  const [canUndoFile, setCanUndoFile] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [oldPdfUrl, setOldPdfUrl] = useState<string>("");

  const handleFileChange = (file: any) => {
    if (file) {
      setFile(file);
      setFileExist(true);
    } else {
      setFile(null);
      setFileExist(false);
    }
  };

  const handleUndo = () => {
    pdfUrlField.input.onChange(oldPdfUrl);
    setCanUndoFile(false);
    setOldPdfUrl("");
  };

  const handleCancel = () => {
    ownInput.input.onChange(null);
    setFile(null);
    setFileExist(false);
  };

  const handleRemove = () => {
    setOldPdfUrl(pdfUrlField.input.value);
    ownInput.input.onChange(null);
    pdfUrlField.input.onChange(null);
    setCanUndoFile(true);
    setFile(null);
    setFileExist(false);
    setOpen(false);
  };

  const CustomButtonRemove = () => {
    return (
      <>
        <Button
          label={`Remove`}
          className={classes.dangerButton}
          onClick={() => setOpen(true)}
        />
        <Dialog
          open={open}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className={classes.dialog}
          maxWidth="sm"
        >
          <DialogTitle id="alert-dialog-title">Remove Confirmation</DialogTitle>
          <DialogContent>
            <Typography gutterBottom>
              Are you sure want to remove this Document?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleRemove}
              color="primary"
              undoable={false}
              label="Yes"
            />
            <Button
              onClick={() => setOpen(false)}
              color="secondary"
              label="Cancel"
              className={classes.cancelButton}
            />
          </DialogActions>
        </Dialog>
      </>
    );
  };

  return (
    <>
      <FileInput
        source={source}
        label={label ? label : "Supporting Document"}
        accept="application/pdf"
        maxSize={maxSize}
        placeholder="Drop a PDF file here to upload, or click to select one"
        onChange={handleFileChange}
        isRequired={isRequired}
        validate={
          isRequired && !pdfUrlField.input.value ? required() : undefined
        }
      >
        <FileField source="src" title="title" target="_blank" />
      </FileInput>

      {canUndoFile && <Button label={`Undo Remove`} onClick={handleUndo} />}

      {(fileExist || pdfUrlField.input.value) && (
        <>
          {fileExist && (
            <Button
              label={`Cancel`}
              className={classes.dangerButton}
              onClick={handleCancel}
            />
          )}
          {!isRequired && pdfUrlField.input.value && <CustomButtonRemove />}
          <CustomPdfPreview
            file={
              file
                ? file
                : pdfUrlField.input.value
                ? pdfUrlField.input.value
                : undefined
            }
          />
        </>
      )}

      {uploadProgress > 0 && (
        <div style={{ width: "100%" }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="body2">
            {" "}
            {uploadProgress < 100 ? "Uploading..." : "Uploaded"}{" "}
            {uploadProgress}%{" "}
          </Typography>
          <Typography variant="body2">
            Please wait until the upload is complete, don't change or close this
            page.
          </Typography>
        </div>
      )}
    </>
  );
};

export default CustomPdfInput;
